import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Progress } from "@/components/ui/progress";
import { FileText, Wallet, CalendarDays } from "lucide-react";
import axios, { API } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import StatusBadge from "@/components/shared/StatusBadge";
import LoadingScreen from "@/components/shared/LoadingScreen";
import { Button } from "@/components/ui/button";

function formatDate(d) { return d ? new Date(d).toLocaleDateString("id-ID", { day: "numeric", month: "long", year: "numeric" }) : "-"; }

export default function JamaahDashboard() {
  const { user } = useAuth();
  const [data, setData] = useState(null);
  const navigate = useNavigate();

  useEffect(() => {
    axios.get(`${API}/jamaah/dashboard`).then(({ data }) => setData(data)).catch(() => setData(null));
  }, []);

  if (!data) return <LoadingScreen />;

  return (
    <div data-testid="jamaah-dashboard-page" className="space-y-6">
      <div className="rounded-2xl bg-gradient-to-r from-[#0F6B4F] to-[#1677B8] p-7 text-white">
        <h1 className="font-heading text-2xl font-bold">Assalamu'alaikum, {user?.name}</h1>
        <p className="mt-1 text-sm text-white/85">Selamat datang di dashboard KBIHU Muhammadiyah Surabaya. Berikut ringkasan persiapan ibadah Anda.</p>
        <div className="mt-5 flex items-center gap-3">
          <StatusBadge status={data.profile?.status} />
          {data.program && <span className="text-sm text-white/90">{data.program.name}</span>}
        </div>
      </div>

      {!data.program && (
        <div className="rounded-2xl border border-dashed border-[#0F6B4F]/30 bg-white p-6 text-center" data-testid="jamaah-no-program-notice">
          <p className="text-sm text-[#64748B]">Anda belum memilih program. Silakan pilih program Haji atau Umrah.</p>
          <Button data-testid="jamaah-choose-program-button" onClick={() => navigate("/program")} className="mt-4 rounded-full bg-[#0F6B4F] text-white hover:bg-[#084C3A]">Pilih Program</Button>
        </div>
      )}

      <div className="rounded-2xl bg-white p-6 shadow-[0_2px_10px_rgba(15,107,79,0.05)]" data-testid="jamaah-preparation-progress">
        <div className="flex items-center justify-between">
          <h3 className="font-heading text-base font-semibold text-[#1F2937]">Persiapan Ibadah</h3>
          <span className="font-heading text-lg font-bold text-[#0F6B4F]">{data.preparation_progress}%</span>
        </div>
        <Progress value={data.preparation_progress} className="mt-3 h-2.5" />
        <p className="mt-2 text-xs text-[#64748B]">{data.preparation_progress}% Persiapan Selesai</p>
      </div>

      <div className="grid grid-cols-1 gap-5 sm:grid-cols-3">
        <div className="rounded-2xl bg-white p-5 shadow-[0_2px_10px_rgba(15,107,79,0.05)]" data-testid="jamaah-document-progress-card">
          <FileText className="h-6 w-6 text-[#0F6B4F]" />
          <p className="mt-3 font-heading text-xl font-bold text-[#1F2937]">{data.document_progress.verified}/{data.document_progress.total}</p>
          <p className="text-sm text-[#64748B]">Dokumen Terverifikasi</p>
        </div>
        <div className="rounded-2xl bg-white p-5 shadow-[0_2px_10px_rgba(15,107,79,0.05)]" data-testid="jamaah-payment-progress-card">
          <Wallet className="h-6 w-6 text-[#1677B8]" />
          <p className="mt-3 font-heading text-xl font-bold text-[#1F2937]">{data.payment_progress}%</p>
          <p className="text-sm text-[#64748B]">Progres Pembayaran</p>
        </div>
        <div className="rounded-2xl bg-white p-5 shadow-[0_2px_10px_rgba(15,107,79,0.05)]" data-testid="jamaah-departure-card">
          <CalendarDays className="h-6 w-6 text-[#0F6B4F]" />
          <p className="mt-3 font-heading text-xl font-bold text-[#1F2937]">{data.program ? formatDate(data.program.departure_date) : "-"}</p>
          <p className="text-sm text-[#64748B]">Tanggal Keberangkatan</p>
        </div>
      </div>
    </div>
  );
}
